import { ArrowDownRightIcon, ArrowUpRightIcon, ChevronDownIcon, PencilIcon } from 'lucide-react'
import { useMemo } from 'react'

import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { UnderlinedTooltip } from 'components/ui/UnderlinedTooltip'
import { DropdownMenu, IconButton } from 'slab'
import { cn, formatMoney } from 'utils'

type Props = {
  label: string
  cost: number
  currentCost?: number
  tooltip?: string | React.ReactNode
  isLoading?: boolean
  isEditable?: boolean
  onClickEdit?: () => void
  testId?: string
}

export default function LineItem ({
  label,
  cost,
  currentCost,
  tooltip,
  isLoading,
  isEditable = false,
  onClickEdit,
  testId
}: Props) {
  const valueIcon = useMemo(() => {
    if (!currentCost) {
      return null
    }

    return cost > currentCost
      ? <ArrowUpRightIcon className='w-[14px] h-[14px] text-red-400' data-testid='arrow-up-right-icon' />
      : <ArrowDownRightIcon className='w-[14px] h-[14px] text-green-400' data-testid='arrow-down-right-icon' />
  }, [currentCost, cost])

  const dropDownMenu = (
    <DropdownMenu trigger={() => (
      <IconButton
        icon={ChevronDownIcon}
        label='Edit'
      />
    )}
    >
      <DropdownMenu.Item
        leftIcon={PencilIcon}
        onSelect={onClickEdit}
      >
        Edit
      </DropdownMenu.Item>
    </DropdownMenu>
  )

  return (
    <div className='flex flex-col'>
      <div className='flex flex-row items-center'>
        <UnderlinedTooltip
          content={tooltip}
          className={cn('body-sm text-gray-500', { 'mr-2': !isEditable })}
        >
          {label}
        </UnderlinedTooltip>
        {isEditable && dropDownMenu}
        <div className='flex-1 border-b-2 border-dotted border-gray-200 mr-2' />
        <div className='flex flex-row items-center gap-1'>
          {valueIcon && valueIcon}
          {isLoading
            ? (
              <div data-testid={`${testId}-skeleton`}>
                <SkeletonItem heightClass='h-4' widthClass='w-12' />
              </div>
              )
            : (
              <span className='body-sm-bold text-gray-800' data-testid={testId}>
                {formatMoney(cost)}
              </span>
              )}
        </div>
      </div>
      {!!currentCost && currentCost > 0 && (
        <div className='self-end'>
          <span className='caption text-gray-500'>
            {formatMoney(currentCost)} currently
          </span>
        </div>
      )}
    </div>
  )
}
